'use client';
import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';

interface CaptionEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}

const CaptionEditor: React.FC<CaptionEditorProps> = ({ value, onChange, placeholder }) => {
    const editorRef = useRef<HTMLDivElement>(null);

    const handleInput = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const cleanBrowserExtensionAttributes = (element: HTMLElement) => {
        // Remove attributes added by browser extensions
        const extensionAttributes = ['bis_skin_checked', 'data-gr-ext-installed', 'data-lt-installed'];

        element.querySelectorAll('*').forEach((el) => {
            if (el instanceof HTMLElement) {
                extensionAttributes.forEach(attr => {
                    if (el.hasAttribute(attr)) {
                        el.removeAttribute(attr);
                    }
                });
            }
        });
    };

    const handleBlur = () => {
        if (editorRef.current) {
            const text = editorRef.current.innerText || '';

            // Clean browser extension attributes first
            cleanBrowserExtensionAttributes(editorRef.current);

            if (text.trim() === '') {
                // Clear content to show placeholder
                editorRef.current.innerHTML = '';
                onChange('');
            } else if (text.includes('#')) {
                // Highlight hashtags only on blur
                // Work with text nodes to avoid corrupting HTML structure
                const walker = document.createTreeWalker(
                    editorRef.current,
                    NodeFilter.SHOW_TEXT,
                    null
                );

                const textNodes: Text[] = [];
                let node: Node | null;
                while ((node = walker.nextNode())) {
                    textNodes.push(node as Text);
                }

                // Process each text node
                textNodes.forEach((textNode) => {
                    const content = textNode.textContent || '';
                    if (content.includes('#')) {
                        // Create a temporary div to parse the highlighted HTML
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = content.replace(
                            /#(\S+)/g,
                            '<span style="color: #1976d2; font-weight: 500;">#$1</span>'
                        );

                        // Replace the text node with the new nodes
                        const fragment = document.createDocumentFragment();
                        while (tempDiv.firstChild) {
                            fragment.appendChild(tempDiv.firstChild);
                        }
                        textNode.replaceWith(fragment);
                    }
                });

                onChange(editorRef.current.innerHTML);
            } else {
                // Even if no hashtags, still update to clean the HTML
                onChange(editorRef.current.innerHTML);
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();

        // Get pasted HTML with formatting
        const html = e.clipboardData.getData('text/html');
        const text = e.clipboardData.getData('text/plain');

        // Use HTML if available (preserves formatting), otherwise use plain text
        let pasteContent = html || text;

        if (pasteContent) {
            // Create a temporary div to manipulate the pasted content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = pasteContent;

            // Remove font-family and font-size styling
            const fontTags = Array.from(tempDiv.querySelectorAll('font'));
            fontTags.forEach((font) => {
                const span = document.createElement('span');
                span.innerHTML = font.innerHTML;
                font.replaceWith(span);
            });

            // Remove font-family and font-size from style attributes
            const allElements = tempDiv.querySelectorAll('*');
            allElements.forEach((el) => {
                if (el instanceof HTMLElement && el.style) {
                    el.style.fontFamily = '';
                    el.style.fontSize = '';
                }
            });

            // Insert at cursor position
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.deleteContents();

                const fragment = document.createDocumentFragment();
                let node;
                while ((node = tempDiv.firstChild)) {
                    fragment.appendChild(node);
                }

                range.insertNode(fragment);

                // Move cursor to end of pasted content
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }

            handleInput();
        }
    };

    // Set initial content
    useEffect(() => {
        if (editorRef.current && !editorRef.current.innerHTML && value) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    return (
        <Box
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            onBlur={handleBlur}
            onPaste={handlePaste}
            suppressContentEditableWarning
            sx={{
                minHeight: 'auto',
                padding: '12px 0',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '1.6',
                outline: 'none',
                color: 'grayscale.gray07',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                '&:empty:before': {
                    content: `"${placeholder || '내용을 입력하세요.'}"`,
                    color: 'grayscale.gray04',
                },
                '& strong, & b': {
                    fontWeight: 600,
                },
                '& em, & i': {
                    fontStyle: 'italic',
                },
                '& ul, & ol': {
                    paddingLeft: '24px',
                    margin: '8px 0',
                },
                '& li': {
                    marginBottom: '4px',
                },
                '& p': {
                    margin: '8px 0',
                },
                '& a': {
                    color: '#1976d2',
                    textDecoration: 'underline',
                },
            }}
        />
    );
};

export default CaptionEditor;
